const HERO_IMAGE =
  'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=1600&q=80';

const RELATED_GUIDES = [
  {
    slug: 'goalkeeper-soleus-achilles-kt-tape-pre-game',
    title: 'How to Apply Achilles KT Tape for a Soleus Injury Before a Soccer Goalkeeper Match',
    blurb: 'keeper-specific pre-game taping with Achilles and soleus strips.',
  },
  {
    slug: 'goalkeeper-achilles-soleus-pain-blood-flow-loading',
    title:
      'Goalkeeper Achilles & Soleus Pain: Blood-Flow Warm-Up & Gradual Tendon Loading',
    blurb: 'circulation-first warm-up and loading progressions for keepers.',
  },
  {
    slug: 'soccer-field-injury-remedies-calf-hamstring',
    title: 'On-Field Remedies for Soccer Calf, Soleus & Hamstring Injuries',
    blurb: 'first aid when pain strikes during a match.',
  },
  {
    slug: '7v7-soccer-formation-over-35-guide',
    title: '7v7 Soccer Formation Guide for Players 35+',
    blurb: 'pace management and recovery for adult leagues.',
  },
  {
    slug: 'soccer-defender-drills-free-youtube-videos',
    title: 'Soccer Defender Drills + Free YouTube Training Videos',
    blurb: 'position training with load management in mind.',
  },
];

const REFERENCE_LINKS = [
  {
    label: 'KT Tape — calf / soleus application guide',
    url: 'https://www.kttape.com/blogs/instructions/calf',
  },
  {
    label: 'American Academy of Orthopaedic Surgeons — muscle strains',
    url: 'https://orthoinfo.aaos.org/en/diseases--conditions/muscle-strains/',
  },
  {
    label: 'FIFA 11+ injury prevention warm-up',
    url: 'https://inside.fifa.com/health-and-medical/injury-prevention',
  },
];

/** Soleus pain in soccer — KT Tape application and return-to-play guide */
export const SOLEUS_PAIN_KT_TAPE_SOCCER_POST = {
  id: 'featured-soleus-pain-kt-tape-soccer',
  slug: 'soleus-pain-kt-tape-soccer-guide',
  title: 'Soleus Pain in Soccer: How to Use KT Tape & Keep Playing Safely',
  excerpt:
    'The deep lower-calf muscle that fails on the second sprint. Learn how to tape the soleus with kinesiology tape, when it actually helps, and how to modify training so you can stay on the pitch without turning a tweak into a tear.',
  publishedAt: '2026-07-30T12:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Player Wellness',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Related training & wellness guides',
  relatedPosts: RELATED_GUIDES,
  referenceLinks: REFERENCE_LINKS,
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: 'Soleus', label: 'Deep lower-calf muscle' },
        { value: '2 strips', label: 'Typical KT Tape setup' },
        { value: 'Knee bent', label: 'Key taping position' },
        { value: 'See a clinician', label: 'Before full sprint return' },
      ],
    },
    {
      type: 'panel',
      title: 'Why soccer players get soleus pain',
      paragraphs: [
        'The **soleus** sits deep under the big gastrocnemius calf muscle. It runs from below the knee to the Achilles tendon and does most of its work when your **knee is bent** — exactly the position you are in when defending in a low stance, decelerating, or pushing off after a short burst.',
        'Unlike a classic upper-calf (gastrocnemius) tear that pops on the first explosive sprint, soleus strains often sneak up. You feel fine jogging to warm up, then the **second or third sprint** produces a deep ache low on the calf. Weekend warriors who play Saturday league plus a Sunday pickup game are especially vulnerable.',
        'This guide covers **kinesiology tape (KT Tape)** for support and proprioception, plus how to modify play while you heal. It is not a substitute for an in-person exam. Sharp pain, bruising, or inability to walk mean you stop — tape will not fix a Grade II or III tear.',
      ],
      chips: ['Adult rec soccer', 'KT Tape / kinesiology tape', 'Lower calf', 'Not medical advice'],
    },
    {
      type: 'table',
      title: 'Soleus vs. gastrocnemius — know which calf muscle hurts',
      intro: 'Taping and rehab differ depending on which muscle is injured. Location and what makes it worse are your best field clues.',
      headers: ['Feature', 'Soleus (this guide)', 'Gastrocnemius (upper calf)'],
      rows: [
        [
          'Where it hurts',
          'Lower calf, deeper ache — often 2–4 inches above the heel',
          'Upper “meat” of the calf, closer to the knee',
        ],
        [
          'Knee position that hurts most',
          'Bent knee — sole raises, squatting, defending stance',
          'Straight knee — standing calf raises, hopping with leg straight',
        ],
        [
          'Typical soccer trigger',
          'Fatigue after long match, repeated short sprints, cold start',
          'Single explosive sprint, jump, or sudden push-off',
        ],
        [
          'KT Tape focus',
          'Tape with knee bent ~20–30°; support along lower calf',
          'Tape with knee straight; strips higher on calf belly',
        ],
        [
          'Return-to-sprint caution',
          'Feels okay jogging but fails on acceleration — do not trust a slow jog test',
          'Often cannot push off at all from day one if tear is significant',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'What KT Tape can and cannot do for soleus pain',
      paragraphs: [
        'Kinesiology tape is elastic athletic tape designed to lift the skin slightly and provide **sensory feedback** — your brain gets a reminder about where the muscle is and how it is loading. For a mild soleus strain (Grade I), that can reduce perceived pain during light activity and remind you not to overload the area.',
      ],
      list: [
        '**Can help:** mild strains, chronic tightness after clearance, proprioception during return-to-play, complementing rehab exercises.',
        '**Cannot replace:** rest in the acute phase (first 48–72 hours), physio assessment, or strength work before full sprinting.',
        '**Will not fix:** Achilles tendon rupture, complete muscle tear, stress fracture, DVT, or pain that worsens every session.',
        '**Skin note:** shave or trim hair if needed; remove tape slowly with oil if irritated. Do not apply on broken skin.',
      ],
      callout:
        'Tape is a tool, not a permission slip. If pain increases during a game after taping, come off immediately — the tape did its job by telling you the tissue is not ready.',
    },
    {
      type: 'panel',
      title: 'What you need before taping',
      list: [
        '**Kinesiology tape** (KT Tape, RockTape, or equivalent) — pre-cut strips or a roll you can tear.',
        '**Scissors** if using a roll (round the corners so edges do not peel).',
        '**Alcohol wipe or dry towel** to clean skin of lotion, sweat, and turf pellets.',
        '**Chair or bench** so you can sit with knee bent and foot flat on the floor.',
        '**10–15 minutes** before training — tape needs 30–60 minutes to fully adhere before heavy sweating.',
      ],
    },
    {
      type: 'panel',
      title: 'Step-by-step: KT Tape for soleus (two-strip method)',
      intro: 'Apply tape **30–60 minutes before** activity. The soleus is most active with a bent knee, so always tape in that position.',
      orderedList: [
        '**Position** — Sit with the injured leg bent about 20–30° and foot flat on the floor. Point toes slightly toward you (dorsiflexion) only if it is pain-free.',
        '**Strip 1 (anchor)** — Tear the backing in the middle of a full strip. With **0% stretch** on the ends (just the paper-off anchor tabs), place the center on the most tender spot on the lower calf. Lay the tails down along the muscle toward the knee and toward the Achilles with **no stretch** on the last 2 inches of each end.',
        '**Strip 1 (muscle)** — Rub the strip to activate adhesive. The center can have light **15–25% stretch** (about half the max stretch of the tape) over the painful area — never stretch the anchor ends.',
        '**Strip 2 (support)** — Use a second strip in an **I** or slight **Y** shape: start near the Achilles (anchor, no stretch), run up the outer or inner lower calf over the soleus with 15% stretch, finish below the knee (anchor, no stretch).',
        '**Activate** — Rub all strips briskly for 30–60 seconds. Body heat helps adhesion.',
        '**Test** — Stand up, do 5 gentle calf raises with knee bent. Re-tape if edges lift or pain spikes.',
      ],
      callout:
        'Common mistake: taping with a straight leg. The soleus shortens when the knee bends — tape applied straight-legged will bunch and peel when you defend or squat.',
    },
    {
      type: 'panel',
      title: 'Alternative: heel-lock plus lower-calf strip',
      paragraphs: [
        'Some players add a **heel lock** strip around the ankle (figure-8, low stretch) to limit excess dorsiflexion that tugs on a sore soleus during push-off. Use this only if a sports clinician has shown you the pattern — an overly tight heel lock can change your running mechanics.',
        'For most Grade I soleus strains, the two-strip method above is enough. Add compression sleeve over the tape if your league allows it and the sleeve does not cut off circulation.',
      ],
    },
    {
      type: 'videoList',
      title: 'Video: KT Tape calf & soleus application',
      intro: 'These external tutorials demonstrate kinesiology taping on the calf. Adapt for a bent-knee soleus setup as described above.',
      videos: [
        {
          title: 'KT Tape: Calf application',
          channel: 'KT Tape',
          description:
            'Official KT Tape calf taping guide — two strips along each side of the calf with anchor/stretch/anchor technique. Apply with knee bent for soleus focus.',
          url: 'https://www.youtube.com/watch?v=elvYXZUEMDo',
        },
        {
          title: 'Calf strain (gastrocnemius/soleus) kinesiology tape',
          channel: 'John Gibbons',
          description:
            'Physio-led taping for calf strains including soleus — decompression strip placement over the painful area with correct stretch percentage.',
          url: 'https://www.youtube.com/watch?v=u-bRY2TMMZ8',
        },
        {
          title: 'KT Tape: Get ready to tape (basics)',
          channel: 'KT Tape',
          description:
            'How to prep skin, use anchor/stretch/anchor method, and remove tape — watch this first if you are new to kinesiology tape.',
          url: 'https://www.youtube.com/watch?v=KesPHFirD7Q',
        },
      ],
    },
    {
      type: 'table',
      title: 'Taping mistakes that fail on the soccer field',
      headers: ['Mistake', 'What happens', 'Fix'],
      rows: [
        [
          'Stretching the anchor ends',
          'Tape peels within 20 minutes; blisters at ends',
          'Last 2 inches of every strip: zero stretch',
        ],
        [
          'Taping over wet or sweaty skin',
          'Tape rolls off during warm-up jog',
          'Dry skin, apply 45+ min before kickoff',
        ],
        [
          'Straight knee application',
          'Bunches when you bend knee to receive a pass',
          'Sit, knee bent 20–30°, foot flat',
        ],
        [
          'Maximum stretch over tear',
          'Increases compression on inflamed tissue',
          'Light 15–25% stretch on muscle belly only',
        ],
        [
          'Taping instead of resting acute injury',
          'Masks pain, worsens tear',
          'No tape first 48–72 hrs if swollen or bruised',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Playing soccer with soleus pain — what to modify',
      intro: 'If a clinician has cleared you for modified play and pain is mild (1–3 out of 10), use tape as part of a load-management plan — not the whole plan.',
      orderedList: [
        '**Warm up longer** — 15–20 minutes: bike or jog, dynamic leg swings, bent-knee calf raises, progressive accelerations. Cold soleus tissue tears easily.',
        '**Cap sprint volume** — No repeated 40-yard sprints in warm-up. Save max effort for the match.',
        '**Shorten stride early** — Overstriding increases soleus eccentric load. Quick, shorter steps when accelerating.',
        '**Avoid deep defending stance** for long periods — switch marking assignments or play higher if possible.',
        '**Sub yourself** — Plan 10–15 minute blocks instead of 45 straight. Fatigue is when soleus re-injures.',
        '**No back-to-back match days** until you can sprint pain-free two days in a row in training.',
        '**Ice after** — 15 minutes even if the game felt fine. Soleus inflammation often spikes hours later.',
      ],
      callout:
        'If you need tape to get through every session for more than two weeks, you do not have a taping problem — you have a rehab problem. Book a physio.',
    },
    {
      type: 'panel',
      title: 'When NOT to play — even with tape on',
      list: [
        'Sharp or stabbing pain on the first sprint (not a dull ache).',
        'Pain that rises from 2/10 to 6/10 during the match.',
        'New bruising or swelling on the lower calf.',
        'Limping that teammates can see from the sideline.',
        'Pain at rest or at night that is getting worse each day.',
        'Numbness, tingling, or calf pain with shortness of breath (rule out blood clot — seek urgent care).',
        'Any suspicion of Achilles involvement — point tenderness on the tendon itself, not the muscle.',
      ],
    },
    {
      type: 'panel',
      title: 'Off-field rehab while you tape and play (modified)',
      intro: 'Run these only if pain-free or at clinician direction. Stop any exercise that reproduces match-day sharp pain.',
      list: [
        '**Bent-knee soleus raises** — Both feet, knees bent, rise onto toes slowly (3 sec up, 3 sec down). 2×15 when pain-free.',
        '**Single-leg bent-knee calf raise** — Progress only when double-leg is easy. Goal: 20 reps before return to full sprint.',
        '**Eccentric soleus drops** — Rise on two legs, lower on one over 4 seconds. Gold standard for calf rehab.',
        '**Foam roll gastroc, not soleus** — Deep rolling on a fresh soleus tear can worsen bleeding. Gentle upper-calf roll only.',
        '**Ankle mobility** — Knee-to-wall dorsiflexion drills without pain at the lower calf.',
        '**Sleep** — Soleus recovers during rest. Late-night league plus early morning workouts stall healing.',
      ],
    },
    {
      type: 'panel',
      title: 'Return-to-play checklist (after clinician clearance)',
      list: [
        'Walk and climb stairs pain-free for 3+ days.',
        '25 single-leg bent-knee calf raises with control.',
        'Jog 10 minutes — no pain next morning.',
        '5×20-yard accelerations at 70%, then 90%, then 100% — each step on a separate day if needed.',
        'Change-of-direction drills (shuttle, figure-8) without pain within 24 hours.',
        'Full training session — small-sided games — before competitive match minutes.',
        'Tape optional at this stage; many players drop tape once strength is symmetric.',
      ],
    },
    {
      type: 'panel',
      title: 'Prevention for your next season',
      list: [
        'Add **bent-knee soleus raises** 2× per week year-round — not just after injury.',
        'Run **FIFA 11+** or equivalent before every training and match.',
        'Do not skip the warm-up jog on cold Ohio evenings — soleus injuries spike in the first 15 minutes.',
        'Rotate cleats: worn studs change push-off mechanics and overload the lower calf.',
        'Hydrate in summer leagues — cramping precedes many soleus tears.',
        'Track minutes if you play multiple teams. The soleus fails from volume, not just intensity.',
      ],
      callout:
        'The best tape job is the one you eventually do not need. Build soleus endurance in the gym so match day is not the stress test.',
    },
  ],
};
